import React, { useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import {
  OrderCard as StyledOrderCard,
  OrderCardHeader,
  OrderCardHeaderMain,
  OrderThumbStrip,
  OrderThumb,
  OrderThumbMore,
  OrderHeaderInfo,
  OrderTitleRow,
  OrderTitle,
  OrderIdButton,
  OrderMeta,
  OrderBadges,
  StatusBadge,
  OrderHeaderPricing,
  OrderTotal,
  OrderItemCount,
  ExpandButton,
  OrderDetails,
  DetailsGrid,
  DetailsSection,
  SectionHeading,
  ItemsList,
  OrderItem,
  OrderItemImage,
  OrderItemBody,
  OrderItemName,
  OrderItemMeta,
  OrderItemPrice,
  TotalsBlock,
  TotalLine,
  GrandTotalLine,
  InfoBlock,
  InfoLine,
  InfoLabel,
  InfoValue,
  ReceiptLink,
  CopyFeedback,
} from "styles/pages/dtf-transfers/orders";
import {
  formatOrderDate,
  formatCad,
  getOrderTotal,
  getOrderItems,
  getItemQuantity,
  getTotalItemCount,
  getOrderNumber,
  getPaymentStatus,
  getCardDisplay,
  formatShippingMethod,
  formatShippingAddress,
  getSizeLabel,
  getArtworkOptionLabels,
  getReceiptUrl,
} from "./helpers";

const getStatusTone = (status) => {
  const normalized = (status || "").toLowerCase();
  if (["succeeded", "paid", "approve", "approved"].includes(normalized)) {
    return "success";
  }
  if (["pending", "processing"].includes(normalized)) {
    return "pending";
  }
  if (["failed", "declined", "rejected"].includes(normalized)) {
    return "error";
  }
  return "neutral";
};

const OrderCard = ({ order, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [copied, setCopied] = useState(false);

  const items = getOrderItems(order);
  const itemCount = getTotalItemCount(items);
  const checkout = order?.checkout ?? {};
  const currency = order?.currency || checkout.currency || "CAD";
  const paymentStatus = getPaymentStatus(order);
  const cardDisplay = getCardDisplay(order);
  const shippingAddress = formatShippingAddress(checkout.shippingAddress);
  const receiptUrl = getReceiptUrl(order);
  const orderNumber = getOrderNumber(order);
  const visibleThumbs = items.slice(0, 3);
  const extraThumbCount = Math.max(items.length - visibleThumbs.length, 0);
  const taxRatePercent =
    checkout.taxRate != null ? Math.round(checkout.taxRate * 100) : null;

  const handleCopyId = async (event) => {
    event.stopPropagation();
    if (!orderNumber || !navigator?.clipboard) return;

    try {
      await navigator.clipboard.writeText(orderNumber);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore clipboard errors
    }
  };

  return (
    <StyledOrderCard $expanded={expanded}>
      <OrderCardHeader
        type="button"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
      >
        <OrderCardHeaderMain>
          <OrderThumbStrip aria-hidden>
            {visibleThumbs.map((item, index) => (
              <OrderThumb
                key={item.id ?? item.cartRecordId ?? index}
                src={item.previewUrl || item.secureUrl}
                alt=""
              />
            ))}
            {extraThumbCount > 0 ? (
              <OrderThumbMore>+{extraThumbCount}</OrderThumbMore>
            ) : null}
            {items.length === 0 ? <OrderThumb as="div" /> : null}
          </OrderThumbStrip>

          <OrderHeaderInfo>
            <OrderTitleRow>
              <OrderTitle>Order #{orderNumber ?? "—"}</OrderTitle>
              {orderNumber ? (
                <OrderIdButton type="button" onClick={handleCopyId}>
                  <ContentCopyIcon fontSize="inherit" />
                  Copy number
                  {copied ? <CopyFeedback>Copied</CopyFeedback> : null}
                </OrderIdButton>
              ) : null}
            </OrderTitleRow>

            <OrderMeta>{formatOrderDate(order)}</OrderMeta>

            <OrderBadges>
              <StatusBadge $tone={getStatusTone(paymentStatus)}>
                Payment: {paymentStatus}
              </StatusBadge>
              {checkout.rushSameDay ? (
                <StatusBadge $tone="pending">Rush Same Day</StatusBadge>
              ) : null}
              {checkout.shippingMethod ? (
                <StatusBadge $tone="neutral">
                  {formatShippingMethod(checkout.shippingMethod)}
                </StatusBadge>
              ) : null}
            </OrderBadges>
          </OrderHeaderInfo>
        </OrderCardHeaderMain>

        <OrderHeaderPricing>
          <OrderTotal>{formatCad(getOrderTotal(order), currency)}</OrderTotal>
          <OrderItemCount>
            {itemCount} item{itemCount === 1 ? "" : "s"}
          </OrderItemCount>
          <ExpandButton $expanded={expanded} aria-hidden>
            {expanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </ExpandButton>
        </OrderHeaderPricing>
      </OrderCardHeader>

      {expanded ? (
        <OrderDetails>
          <DetailsGrid>
            <DetailsSection>
              <SectionHeading>Items</SectionHeading>
              {items.length === 0 ? (
                <InfoLine>No items found for this order.</InfoLine>
              ) : (
                <ItemsList>
                  {items.map((item, index) => {
                    const quantity = getItemQuantity(item);
                    const sizeLabel = getSizeLabel(item);
                    const optionLabels = getArtworkOptionLabels(item);

                    return (
                      <OrderItem key={item.id ?? item.cartRecordId ?? index}>
                        <OrderItemImage
                          src={item.previewUrl || item.secureUrl}
                          alt={item.fileName || "Order artwork"}
                        />
                        <OrderItemBody>
                          <OrderItemName>
                            {item.productType === "gang-sheet"
                              ? "DTF Gang Sheet Builder"
                              : item.fileName || "Artwork"}
                          </OrderItemName>
                          <OrderItemMeta>
                            {sizeLabel ? `${sizeLabel} · ` : ""}
                            Qty {quantity}
                            {optionLabels.length
                              ? ` · ${optionLabels.join(" · ")}`
                              : ""}
                          </OrderItemMeta>
                        </OrderItemBody>
                        <OrderItemPrice>
                          {formatCad(item.total, currency)}
                        </OrderItemPrice>
                      </OrderItem>
                    );
                  })}
                </ItemsList>
              )}
            </DetailsSection>

            <DetailsSection>
              <SectionHeading>Order summary</SectionHeading>
              <TotalsBlock>
                {checkout.rushSameDay && checkout.itemsSubtotal != null ? (
                  <>
                    <TotalLine>
                      <span>
                        Items · {itemCount} item
                        {itemCount === 1 ? "" : "s"}
                      </span>
                      <span>{formatCad(checkout.itemsSubtotal, currency)}</span>
                    </TotalLine>
                    <TotalLine>
                      <span>Rush Same Day</span>
                      <span>
                        {formatCad(
                          checkout.rushSameDayFee ?? checkout.itemsSubtotal,
                          currency,
                        )}
                      </span>
                    </TotalLine>
                  </>
                ) : null}
                <TotalLine>
                  <span>
                    Subtotal
                    {checkout.rushSameDay && checkout.itemsSubtotal == null
                      ? " (Rush Same Day)"
                      : ""}
                    {!checkout.rushSameDay
                      ? ` · ${itemCount} item${itemCount === 1 ? "" : "s"}`
                      : ""}
                  </span>
                  <span>{formatCad(checkout.subtotal, currency)}</span>
                </TotalLine>
                <TotalLine>
                  <span>
                    Shipping
                    {checkout.shippingMethod
                      ? ` (${formatShippingMethod(checkout.shippingMethod)})`
                      : ""}
                  </span>
                  <span>{formatCad(checkout.shippingCost, currency)}</span>
                </TotalLine>
                {checkout.taxAmount != null ? (
                  <TotalLine>
                    <span>
                      Tax{taxRatePercent != null ? ` (${taxRatePercent}%)` : ""}
                    </span>
                    <span>{formatCad(checkout.taxAmount, currency)}</span>
                  </TotalLine>
                ) : null}
                <GrandTotalLine>
                  <span>Total ({currency})</span>
                  <span>{formatCad(getOrderTotal(order), currency)}</span>
                </GrandTotalLine>
              </TotalsBlock>
            </DetailsSection>

            {shippingAddress ? (
              <DetailsSection>
                <SectionHeading>
                  <LocalShippingOutlinedIcon fontSize="small" />
                  Shipping to
                </SectionHeading>
                <InfoBlock>
                  {shippingAddress.name ? (
                    <InfoLine>{shippingAddress.name}</InfoLine>
                  ) : null}
                  {shippingAddress.company ? (
                    <InfoLine>{shippingAddress.company}</InfoLine>
                  ) : null}
                  {shippingAddress.street ? (
                    <InfoLine>{shippingAddress.street}</InfoLine>
                  ) : null}
                  {shippingAddress.cityLine ? (
                    <InfoLine>{shippingAddress.cityLine}</InfoLine>
                  ) : null}
                  {shippingAddress.phone ? (
                    <InfoLine>{shippingAddress.phone}</InfoLine>
                  ) : null}
                  {checkout.contact?.email ? (
                    <InfoLine>{checkout.contact.email}</InfoLine>
                  ) : null}
                </InfoBlock>
              </DetailsSection>
            ) : null}

            <DetailsSection>
              <SectionHeading>
                <CreditCardOutlinedIcon fontSize="small" />
                Payment
              </SectionHeading>
              <InfoBlock>
                {cardDisplay ? (
                  <InfoLine>
                    <InfoLabel>Card</InfoLabel>
                    <InfoValue>{cardDisplay}</InfoValue>
                  </InfoLine>
                ) : null}
                {order?.charge?.id ? (
                  <InfoLine>
                    <InfoLabel>Charge</InfoLabel>
                    <InfoValue>{order.charge.id}</InfoValue>
                  </InfoLine>
                ) : null}
                {receiptUrl ? (
                  <ReceiptLink
                    href={receiptUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => event.stopPropagation()}
                  >
                    View Stripe receipt
                    <OpenInNewIcon fontSize="inherit" />
                  </ReceiptLink>
                ) : null}
              </InfoBlock>
            </DetailsSection>
          </DetailsGrid>
        </OrderDetails>
      ) : null}
    </StyledOrderCard>
  );
};

export default OrderCard;
